// src/components/Nav.js
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, fonts, fontSizes, fontWeights, mq, space, ease, duration } from '../styles/tokens';
import { Icon } from './Icon';
import { MobileMenu } from './MobileMenu';

const NAV_LINKS = [
  { href: '#home',    id: 'home',    label: 'Home' },
  { href: '#about',   id: 'about',   label: 'About' },
  { href: '#work',    id: 'work',    label: 'Work' },
  { href: '#skills',  id: 'skills',  label: 'Skills' },
  { href: '#contact', id: 'contact', label: 'Contact' },
];

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 ${space[4]};
  background: ${(p) => (p.$scrolled ? 'rgba(17, 17, 24, 0.8)' : 'transparent')};
  backdrop-filter: ${(p) => (p.$scrolled ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${(p) => (p.$scrolled ? 'blur(10px)' : 'none')};
  border-bottom: ${(p) => (p.$scrolled ? `1px solid ${colors.border}` : '1px solid transparent')};
  transform: translateY(${(p) => (p.$hidden ? '-100%' : '0')});
  transition: transform ${duration.base} ${ease.soft},
              background ${duration.base} ${ease.soft},
              border-color ${duration.base} ${ease.soft};
  ${mq.md} { height: 64px; padding: 0 ${space[5]}; }
`;

const Brand = styled.a`
  font-family: ${fonts.mono};
  font-size: ${fontSizes.md};
  font-weight: ${fontWeights.semibold};
  color: ${colors.text};
  span.caret {
    color: ${colors.accent};
    animation: blink-caret 0.8s step-end infinite;
  }
`;

const Links = styled.nav`
  display: none;
  margin-left: auto;
  gap: ${space[4]};
  align-items: center;
  ${mq.md} { display: flex; }
`;

const NavLink = styled.a`
  position: relative;
  font-size: ${fontSizes.sm};
  color: ${(p) => (p.$active ? colors.accent : colors.textDim)};
  &:hover { color: ${colors.accent}; }
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -4px;
    height: 2px;
    width: ${(p) => (p.$active ? '100%' : '0%')};
    background: ${colors.accent};
    transition: width ${duration.base} ${ease.soft};
  }
  &:hover::after { width: 100%; }
`;

const Separator = styled.span`
  width: 1px;
  height: 18px;
  background: ${colors.border};
`;

const IconLink = styled.a`
  color: ${colors.textDim};
  padding: ${space[1]};
  display: inline-flex;
  &:hover { color: ${colors.accent}; }
`;

const Hamburger = styled.button`
  margin-left: auto;
  color: ${colors.text};
  padding: ${space[1]};
  ${mq.md} { display: none; }
`;

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState('home');

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      if (menuOpen) { lastY = y; return; }
      if (y > 200 && y > lastY) setHidden(true);
      else setHidden(false);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [menuOpen]);

  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (!sections.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Header $scrolled={scrolled} $hidden={hidden && !menuOpen} role="banner">
        <Brand href="#home" aria-label="Fabio Lillman — home">
          fabio.dev<span className="caret">_</span>
        </Brand>
        <Links aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.id} href={l.href} $active={activeId === l.id} aria-current={activeId === l.id ? 'true' : undefined}>
              {l.label}
            </NavLink>
          ))}
          <Separator aria-hidden="true" />
          <IconLink href="https://github.com/Fabiolillman" target="_blank" rel="noopener noreferrer" aria-label="GitHub"><Icon name="github" /></IconLink>
          <IconLink href="https://www.linkedin.com/in/fabio-lillman/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><Icon name="linkedin" /></IconLink>
        </Links>
        <Hamburger onClick={() => setMenuOpen(true)} aria-label="Open menu">
          <Icon name="menu" size={24} />
        </Hamburger>
      </Header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
